import { Avatar, Button, Group, Modal, Stack, Text } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFusePlaceholderMember } from 'src/Hooks/Mutations/useFusePlaceholderMember';
import useFetchPendingPlaceholderMatches from 'src/Hooks/Queries/useFetchPendingPlaceholderMatches';

import styles from './PendingPlaceholderMatchPopup.module.scss';

const PendingPlaceholderMatchPopup = () => {
  const { t } = useTranslation();
  const { data: matches } = useFetchPendingPlaceholderMatches();
  const { mutate: fuse, isLoading } = useFusePlaceholderMember();
  const [dismissedIds, setDismissedIds] = useState<string[]>([]);

  const visibleMatches = (matches ?? []).filter(m => !dismissedIds.includes(m.id));

  useEffect(() => {
    setDismissedIds([]);
  }, [matches]);

  if (visibleMatches.length === 0) return null;

  const currentMatch = visibleMatches[0];

  const handleConfirm = () => {
    fuse(
      { placeholderId: currentMatch.id, assetId: currentMatch.assetId },
      { onSettled: () => setDismissedIds(current => [...current, currentMatch.id]) }
    );
  };

  const handleSkip = () => {
    setDismissedIds(current => [...current, currentMatch.id]);
  };

  return (
    <Modal
      opened
      onClose={handleSkip}
      title={t('placeholderMatch.title', 'Est-ce vous ?')}
      centered
      size="sm"
    >
      <Stack gap="md">
        <Group>
          <Avatar radius="xl">{currentMatch.firstName.charAt(0)}</Avatar>
          <div>
            <Text fw={500}>
              {currentMatch.firstName} {currentMatch.lastName}
            </Text>
            <Text size="sm" c="dimmed">
              {t('placeholderMatch.asset', 'dans "{{assetName}}"', {
                assetName: currentMatch.assetName,
              })}
            </Text>
          </div>
        </Group>
        <Text size="sm" c="dimmed">
          {t(
            'placeholderMatch.description',
            'Un gestionnaire vous a ajouté comme membre non-inscrit avec cette adresse email. Confirmez pour rejoindre {{assetName}} et récupérer vos informations de gouvernance.',
            { assetName: currentMatch.assetName }
          )}
        </Text>
        <Group justify="flex-end" className={styles.actions}>
          <Button variant="outline" onClick={handleSkip} disabled={isLoading}>
            {t('placeholderMatch.skip', "Ce n'est pas moi")}
          </Button>
          <Button onClick={handleConfirm} loading={isLoading}>
            {t('placeholderMatch.confirm', "Oui, c'est moi")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default PendingPlaceholderMatchPopup;
